import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActivityType } from 'types/interfaces/activity';
import { ACTIVITY_TYPE_CONFIG, FILTER_DISPLAY_ORDER } from '../constants';
import './ActivityFilterBar.css';

// The six event-type pills in the results cap (Asana 1219008751039721,
// decisions 3 and 4): the Home Recent Activity pill treatment, single-select,
// one FontAwesome icon per type, no per-type colours. Pause moved to
// LivePulseDashboard. The portal tooltip stays (300ms, below the pill).

/** Portal-based tooltip that escapes overflow:hidden containers */
const PillTooltip: React.FC<{ text: string; anchor: DOMRect }> = ({ text, anchor }) => {
  const left = anchor.left + anchor.width / 2;
  const top = anchor.bottom + 8;

  return createPortal(
    <div
      className="activity-filter-bar__tooltip"
      style={{ left, top }}
    >
      {text}
    </div>,
    document.body
  );
};

interface ActivityFilterBarProps {
  activeTypes: Set<ActivityType>;
  selectType: (type: ActivityType) => void;
}

const ActivityFilterBar: React.FC<ActivityFilterBarProps> = ({ activeTypes, selectType }) => {
  const [tooltip, setTooltip] = useState<{ text: string; rect: DOMRect } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showTooltip = useCallback((text: string, el: HTMLElement) => {
    timerRef.current = setTimeout(() => {
      setTooltip({ text, rect: el.getBoundingClientRect() });
    }, 300);
  }, []);

  const hideTooltip = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setTooltip(null);
  }, []);

  // Clean up timer on unmount
  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  return (
    <div className="activity-filter-bar" role="group" aria-label="Event type">
      {FILTER_DISPLAY_ORDER.map((type) => {
        const config = ACTIVITY_TYPE_CONFIG[type];
        const isActive = activeTypes.has(type);

        return (
          <button
            key={type}
            className={`activity-type-pill${isActive ? ' is-active' : ''}`}
            onClick={() => selectType(type)}
            onMouseEnter={(e) => showTooltip(config.tooltip, e.currentTarget)}
            onMouseLeave={hideTooltip}
            type="button"
            aria-pressed={isActive}
            aria-label={`Filter ${config.label} activities: ${config.tooltip}`}
            data-testid={`activity-pill-${type}`}
          >
            <FontAwesomeIcon icon={config.icon} className="activity-type-pill__icon" />
            {config.label}
          </button>
        );
      })}

      {tooltip && <PillTooltip text={tooltip.text} anchor={tooltip.rect} />}
    </div>
  );
};

export default ActivityFilterBar;
